import React, { useEffect, useState, useMemo } from "react";
import ImageCard from "../ImageCard/ImageCard";
import "./Collections.scss";
import { useNavigate } from "react-router-dom";
import { useGraphQL } from "../../../hooks/useGraphQL";
import { GET_LISTINGS_WITH_MARKET } from "../Market/Market";

const MARKET_V1 = process.env.REACT_APP_MARKETPLACE_ADDRESS_V1?.toLowerCase();

const Collections = ({
  isLoading = false,
  loadingText = "Loading collections...",
  emptyText = "No collections available",
  title,
}) => {
  const navigate = useNavigate();
  const { loading: loadingCollections, data } = useGraphQL(
    GET_LISTINGS_WITH_MARKET,
  );
  const [collectionImages, setCollectionImages] = useState({});

  const uniqueCollections = useMemo(() => {
    const seen = new Map();
    const counts = new Map();

    for (const listing of data?.listings || []) {
      if (listing.market?.toLowerCase() !== MARKET_V1) continue;
      const col = listing.token?.collection;
      if (!col) continue;
      const id = col.id.toLowerCase();
      if (!seen.has(id)) {
        seen.set(id, col);
        counts.set(id, 0);
      }
      counts.set(id, counts.get(id) + Number(listing.quantity || 1));
    }

    return Array.from(seen.values()).map((col) => ({
      ...col,
      totalListedQuantity: counts.get(col.id.toLowerCase()) || 0,
    }));
  }, [data]);
  const gateways = [
    "https://funooni-public.mypinata.cloud/ipfs/",
    "https://ipfs.io/ipfs/",
    "https://cloudflare-ipfs.com/ipfs/",
    "https://gateway.pinata.cloud/ipfs/",
  ];

  const safeImage = (uri, gateway) => {
    if (!uri) return "";

    if (uri.startsWith("ipfs://")) {
      const hash = uri.replace("ipfs://", "");
      return `${gateway}${hash}`;
    }

    return uri;
  };
  const fetchIPFSJson = async (uri) => {
    if (!uri) throw new Error("Invalid IPFS URI");

    let lastError = null;

    for (const gateway of gateways) {
      try {
        const url = safeImage(uri, gateway);

        const res = await fetch(url, {
          cache: "force-cache",
        });

        if (!res.ok) {
          throw new Error(`Gateway failed: ${gateway}`);
        }

        return await res.json();
      } catch (err) {
        console.warn(`❌ ${gateway} failed`);
        lastError = err;
      }
    }

    throw lastError;
  };
  const getSafeImage = async (uri) => {
    if (!uri) return "";

    for (const gateway of gateways) {
      const url = safeImage(uri, gateway);

      try {
        const res = await fetch(url, {
          method: "HEAD",
        });

        if (res.ok) {
          return url;
        }
      } catch {}
    }

    return "";
  };

  useEffect(() => {
    const loadCollectionImages = async () => {
      if (!uniqueCollections.length) return;

      const result = {};

      await Promise.all(
        uniqueCollections.map(async (col) => {
          if (!col.contractURI) return;

          const id = col.id?.toLowerCase();

          try {
            const meta = await fetchIPFSJson(col.contractURI);

            result[id] = await getSafeImage(meta.image);
          } catch (err) {
            console.error("Metadata error", err);
          }
        }),
      );
      setCollectionImages(result);
    };
    loadCollectionImages();
  }, [uniqueCollections]);

  if (isLoading || loadingCollections) return <p>{loadingText}</p>;

  const items = uniqueCollections.map((col) => ({
    id: col.id.toLowerCase(),
    name: col.name,
    image: collectionImages[col.id.toLowerCase()],
    description: col.description,
    items: col.totalListedQuantity,
  }));

  return (
    <>
      <div className="recent">
        <h2 className="recent__title">{title}</h2>
      </div>

      <section className="Collections-section">
        {items.length > 0 ? (
          items
            .slice(-4)
            .reverse()
            .map((item) => (
              <div
                key={item.id}
                className="collection-card-wrapper"
                onClick={() => {
                  navigate(
                    `/reproduction/collections?country=${item.name}&collection=${item.id}`,
                    { state: { country: item.name, collectionId: item.id } },
                  );
                }}
                style={{ cursor: "pointer" }}
              >
                <ImageCard
                  image={item.image}
                  title={item.name}
                  category={item.name}
                  count={item.items}
                  address={item.id}
                  description={item.description}
                />
              </div>
            ))
        ) : (
          <p>{emptyText}</p>
        )}
      </section>
    </>
  );
};

export default React.memo(Collections);
